import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fmtKm, fmtMin } from '../utils/mockState';
import { fetchRoadGeometry } from '../utils/osrm';
import api from '../api/client';
import { RouteEntity } from '../types';
import {
  cacheDriverRoute,
  flushDriverActions,
  getCachedDriverRoute,
  queueDriverAction,
  queuedDriverActionsCount
} from '../utils/driverOffline';
import './DriverMode.css';
import { TILE_URL_DARK, TILE_URL_DARK_LABELS, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../utils/mapTiles';

declare const L: any;

type GpsStatus = 'checking' | 'active' | 'denied' | 'unavailable' | 'error';
type PushStatus = 'idle' | 'active' | 'blocked' | 'unsupported' | 'unavailable' | 'error';

interface GpsPosition {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  heading: number | null;
  speed: number | null;
}

function applyStopStatusOffline(route: RouteEntity, stopId: string, status: 'concluida' | 'pulada'): RouteEntity {
  const stops = (route.stops || []).map((stop) => (
    stop.id === stopId
      ? { ...stop, status, completedAt: status === 'concluida' ? new Date().toISOString() : null }
      : stop
  ));
  const isComplete = stops.length > 0 && stops.every((stop) => stop.status !== 'pendente');
  return { ...route, stops, status: isComplete ? 'concluida' : route.status };
}

function base64UrlToUint8Array(value: string) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const bytes = atob(base64);
  return Uint8Array.from(bytes, (char) => char.charCodeAt(0));
}

export default function DriverMode() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [route, setRoute] = useState<RouteEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>('checking');
  const [gpsPosition, setGpsPosition] = useState<GpsPosition | null>(null);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [queuedActions, setQueuedActions] = useState(() => queuedDriverActionsCount());
  const [pushStatus, setPushStatus] = useState<PushStatus>('idle');
  const [loadError, setLoadError] = useState('');

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const lastLocationSentAtRef = useRef(0);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get<RouteEntity>(`/routes/${id}`);
      setRoute(data);
      cacheDriverRoute(data);
      setOffline(false);
      setLoadError('');
      if (data.status === 'concluida') setShowCelebration(true);
    } catch {
      const cachedRoute = getCachedDriverRoute(id);
      if (cachedRoute) {
        setRoute(cachedRoute);
        setOffline(true);
        setLoadError('Sem conexão: exibindo a última rota salva neste aparelho.');
      } else {
        setLoadError('Não foi possível carregar a rota. Conecte-se à internet e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const syncQueuedActions = useCallback(async () => {
    if (!navigator.onLine) return;
    const result = await flushDriverActions(async (action) => {
      await api.request({ method: action.method, url: action.url, data: action.body });
    });
    setQueuedActions(result.remaining);
    if (result.sent > 0) await load();
  }, [load]);

  useEffect(() => {
    const handleOnline = () => {
      setOffline(false);
      void syncQueuedActions();
    };
    const handleOffline = () => setOffline(true);
    const handleWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_DRIVER_QUEUE') void syncQueuedActions();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    navigator.serviceWorker?.addEventListener('message', handleWorkerMessage);
    if (navigator.onLine) void syncQueuedActions();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      navigator.serviceWorker?.removeEventListener('message', handleWorkerMessage);
    };
  }, [syncQueuedActions]);

  // O navegador pede a permissão ao motorista e mantém uma única observação
  // ativa. A API recebe, no máximo, uma atualização a cada 15 segundos.
  useEffect(() => {
    if (!route || route.status !== 'em_andamento') return;
    if (!navigator.geolocation) {
      setGpsStatus('unavailable');
      return;
    }

    setGpsStatus('checking');
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const nextPosition: GpsPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
          heading: Number.isFinite(position.coords.heading) ? position.coords.heading : null,
          speed: Number.isFinite(position.coords.speed) ? position.coords.speed : null
        };
        setGpsPosition(nextPosition);
        setGpsStatus('active');

        const now = Date.now();
        if (now - lastLocationSentAtRef.current < 15_000) return;
        lastLocationSentAtRef.current = now;
        if (!navigator.onLine) {
          setOffline(true);
          setQueuedActions(queueDriverAction({ kind: 'location', method: 'post', url: `/routes/${route.id}/location`, body: nextPosition }).length);
          return;
        }

        api.post(`/routes/${route.id}/location`, nextPosition).catch((error) => {
          if (!error.response) {
            setOffline(true);
            setQueuedActions(queueDriverAction({ kind: 'location', method: 'post', url: `/routes/${route.id}/location`, body: nextPosition }).length);
            return;
          }
          setGpsStatus('error');
        });
      },
      (error) => {
        setGpsStatus(error.code === error.PERMISSION_DENIED ? 'denied' : 'error');
      },
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 20_000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [route?.id, route?.status]);

  const stops = route?.stops || [];
  const currentIdx = stops.findIndex((s) => s.status === 'pendente');
  const currentStop = currentIdx >= 0 ? stops[currentIdx] : null;
  const completedCount = stops.filter((s) => s.status === 'concluida').length;

  // Initialize Dark Leaflet Map
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || stops.length === 0) return;
    if (typeof L === 'undefined') return;

    if (mapRef.current) {
      mapRef.current.stop();
      mapRef.current.off();
      mapRef.current.remove();
      mapRef.current = null;
    }

    const focusStop = currentStop || stops[0];
    const map = L.map(container, {
      zoomControl: false,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
      inertia: false
    });
    map.setView([focusStop.deliveryPoint.latitude, focusStop.deliveryPoint.longitude], 15, { animate: false });

    L.tileLayer(TILE_URL_DARK, { maxZoom: TILE_MAX_ZOOM, attribution: TILE_ATTRIBUTION }).addTo(map);
    L.tileLayer(TILE_URL_DARK_LABELS, { maxZoom: TILE_MAX_ZOOM }).addTo(map);

    let cancelled = false;
    const fromIdx = currentIdx >= 0 ? currentIdx : 0;
    const remainingPoints = stops.slice(fromIdx).map((s) => ({ lat: s.deliveryPoint.latitude, lng: s.deliveryPoint.longitude }));
    if (remainingPoints.length > 1) {
      const poly = L.polyline(remainingPoints.map((p) => [p.lat, p.lng]), { color: '#2a9d8f', weight: 6, opacity: 0.9 }).addTo(map);
      fetchRoadGeometry(remainingPoints).then((geometry) => {
        if (cancelled || mapRef.current !== map || !map._loaded || !geometry) return;
        poly.setLatLngs(geometry);
      });
    }

    stops.forEach((s, idx) => {
      const isDone = s.status === 'concluida';
      const isNext = idx === currentIdx;
      const markerHtml = `<div class="stop-marker-mini ${isDone ? 'done' : ''} ${isNext ? 'next' : ''}">${isDone ? '✓' : idx + 1}</div>`;
      const icon = L.divIcon({ className: 'custom-driver-pin', html: markerHtml, iconSize: [32, 32], iconAnchor: [16, 16] });
      L.marker([s.deliveryPoint.latitude, s.deliveryPoint.longitude], { icon }).addTo(map);
    });

    mapRef.current = map;

    return () => {
      cancelled = true;
      map.stop();
      map.off();
      map.remove();
      if (mapRef.current === map) {
        mapRef.current = null;
      }
    };
  }, [stops, currentIdx, currentStop]);

  const centerGPS = () => {
    if (!mapRef.current) return;
    if (gpsPosition) {
      mapRef.current.setView([gpsPosition.latitude, gpsPosition.longitude], 16, { animate: false });
    } else if (currentStop) {
      mapRef.current.setView([currentStop.deliveryPoint.latitude, currentStop.deliveryPoint.longitude], 16, { animate: false });
    }
  };

  const updateStopStatus = async (status: 'concluida' | 'pulada') => {
    if (!route || !currentStop || busy) return;
    setBusy(true);
    const optimisticRoute = applyStopStatusOffline(route, currentStop.id, status);
    const action = { kind: 'stop' as const, method: 'patch' as const, url: `/routes/${route.id}/stops/${currentStop.id}`, body: { status } };

    if (!navigator.onLine) {
      cacheDriverRoute(optimisticRoute);
      setRoute(optimisticRoute);
      setOffline(true);
      setQueuedActions(queueDriverAction(action).length);
      setBusy(false);
      return;
    }

    try {
      const { data } = await api.patch<RouteEntity>(`/routes/${route.id}/stops/${currentStop.id}`, { status });
      setRoute(data);
      cacheDriverRoute(data);
      if (data.status === 'concluida') setShowCelebration(true);
    } catch (error: any) {
      if (!error.response) {
        cacheDriverRoute(optimisticRoute);
        setRoute(optimisticRoute);
        setOffline(true);
        setQueuedActions(queueDriverAction(action).length);
      }
    } finally {
      setBusy(false);
    }
  };

  const enablePushNotifications = async () => {
    if (!route) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPushStatus('unsupported');
      return;
    }

    try {
      const { data } = await api.get<{ publicKey: string }>('/push/public-key');
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushStatus('blocked');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(data.publicKey)
      });
      await api.post('/push/subscriptions', { routeId: route.id, subscription: subscription.toJSON() });
      setPushStatus('active');
    } catch (error: any) {
      setPushStatus(error.response?.status === 503 ? 'unavailable' : 'error');
    }
  };

  const progressPct = stops.length > 0 ? (completedCount / stops.length) * 100 : 0;
  const pushLabel: Record<PushStatus, string> = {
    idle: 'Ativar alertas da rota',
    active: 'Alertas ativados',
    blocked: 'Alertas bloqueados no navegador',
    unsupported: 'Alertas não suportados neste navegador',
    unavailable: 'Alertas aguardam configuração do servidor',
    error: 'Não foi possível ativar os alertas'
  };

  if (loading) {
    return <div className="driver-shell" style={{ display: 'grid', placeItems: 'center', color: '#fff' }}>Carregando rota...</div>;
  }

  if (!route) {
    return (
      <div className="driver-shell" style={{ display: 'grid', placeItems: 'center', color: '#fff' }}>
        <div>
          <p>Rota não encontrada.</p>
          <button className="btn btn-primary" onClick={() => navigate('/rotas')}>Voltar para rotas</button>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-shell">
      <div ref={mapContainerRef} id="driver-map"></div>

      <div className="driver-header">
        <button className="exit" onClick={() => navigate(`/rotas/${route.id}`)} title="Sair do modo motorista">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="info">
          <div className="rname">{route.name}</div>
          <div className="rsub">
            {route.vehicle?.name} ({route.vehicle?.plate}) · {route.vehicle?.driverName || 'Motorista não atribuído'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div className="progress-badge">{completedCount}/{stops.length} entregas</div>
          <div className="progress-badge" title="Status do rastreamento GPS" style={{ color: gpsStatus === 'active' ? '#9ff4d8' : '#ffe39a' }}>
            {gpsStatus === 'active' ? '● GPS' : gpsStatus === 'checking' ? '◌ GPS' : '⚠ GPS'}
          </div>
        </div>
      </div>

      {(offline || queuedActions > 0 || loadError) && (
        <div className="driver-sync-status" role="status">
          {offline ? 'Sem conexão' : 'Conexão restaurada'}
          {queuedActions > 0 ? ` · ${queuedActions} atualização(ões) pendente(s)` : ''}
          {loadError ? ` · ${loadError}` : ''}
        </div>
      )}

      <div className="progress-bar-wrap">
        <div className="progress-bar-fill" style={{ width: `${progressPct}%` }}></div>
      </div>

      <div className="driver-fabs">
        <button className="fab dark" onClick={centerGPS} title={gpsPosition ? 'Centralizar na minha localização' : 'Centralizar no destino atual'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
          </svg>
        </button>
        <button className="fab dark" onClick={() => updateStopStatus('pulada')} title="Pular esta parada" disabled={busy || !currentStop}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="5 4 15 12 5 20 5 4"></polygon>
            <line x1="19" y1="5" x2="19" y2="19"></line>
          </svg>
        </button>
      </div>

      {currentStop && !showCelebration && (
        <div className="next-card">
          <div className="next-card-head">
            <div className="next-card-num">{currentIdx + 1}</div>
            <div>
              <div className="next-card-label">Próxima Entrega</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)' }}>
                {currentStop.deliveryPoint.title}
              </div>
            </div>
          </div>

          <div className="next-card-body">
            <div className="next-card-addr">{currentStop.deliveryPoint.address}</div>
            <div className="next-card-city">{currentStop.weightKg} kg</div>

            <div className="next-card-meta">
              <span className={`meta-chip ${currentStop.priority === 'alta' ? 'prio-alta' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                {currentStop.priority === 'alta' ? 'Prioridade Alta' : 'Prioridade Normal'}
              </span>

              {currentStop.timeWindowStart && (
                <span className="meta-chip">⏰ Janela: {currentStop.timeWindowStart}–{currentStop.timeWindowEnd}</span>
              )}
            </div>
          </div>

          <div className="next-card-actions">
            <button className={`btn-push ${pushStatus === 'active' ? 'active' : ''}`} onClick={enablePushNotifications} disabled={pushStatus === 'active'}>
              {pushStatus === 'active' ? '✓ ' : '🔔 '}{pushLabel[pushStatus]}
            </button>
            <button
              className="btn-nav"
              onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${currentStop.deliveryPoint.latitude},${currentStop.deliveryPoint.longitude}`, '_blank')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
              </svg>
              Navegar (Maps/Waze)
            </button>

            <button className="btn-arrived" onClick={() => updateStopStatus('concluida')} disabled={busy}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              {busy ? 'Salvando...' : 'Cheguei / Concluir entrega'}
            </button>
          </div>
        </div>
      )}

      {showCelebration && (
        <div className="completed-overlay">
          <div className="completed-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h2 className="completed-h">Rota Finalizada!</h2>
          <p className="completed-p">
            {completedCount} de {stops.length} paradas foram processadas.
          </p>

          <div className="completed-metrics">
            <div className="completed-metric">
              <div className="v">{stops.length}</div>
              <div className="k">Entregas</div>
            </div>
            <div className="completed-metric">
              <div className="v">{fmtKm(route.distanceM / 1000)} km</div>
              <div className="k">Distância</div>
            </div>
            <div className="completed-metric">
              <div className="v">{fmtMin(route.durationS / 60)}</div>
              <div className="k">Duração</div>
            </div>
          </div>

          <button className="btn btn-primary btn-lg" onClick={() => navigate('/dashboard')}>
            Voltar ao painel principal
          </button>
        </div>
      )}
    </div>
  );
}
